export interface Extent {
  w: number;
  h: number;
}

export interface Point {
  x: number;
  y: number;
}

/** `scale` is CSS pixels per image pixel; `x`, `y` place the image's top-left corner on the stage. */
export interface ZoomView {
  scale: number;
  x: number;
  y: number;
}

export interface ZoomFrame {
  stage: Extent;
  image: Extent;
  dpr: number;
}

const MAX_ZOOM = 4;
const TOGGLE_TOLERANCE = 0.01;

export const fitScale = (f: ZoomFrame): number =>
  Math.min(f.stage.w / f.image.w, f.stage.h / f.image.h);

// 1:1 means one image pixel per device pixel.
export const oneScale = (f: ZoomFrame): number => 1 / f.dpr;

export function scaleBounds(f: ZoomFrame): [number, number] {
  const fit = fitScale(f);
  const one = oneScale(f);
  return [Math.min(fit, one), Math.max(fit, MAX_ZOOM * one)];
}

const clampScale = (f: ZoomFrame, scale: number): number => {
  const [lo, hi] = scaleBounds(f);
  return Math.min(hi, Math.max(lo, scale));
};

function clampAxis(stage: number, size: number, offset: number): number {
  if (size <= stage) return (stage - size) / 2;
  return Math.min(0, Math.max(stage - size, offset));
}

export function clamp(f: ZoomFrame, v: ZoomView): ZoomView {
  const scale = clampScale(f, v.scale);
  return {
    scale,
    x: clampAxis(f.stage.w, f.image.w * scale, v.x),
    y: clampAxis(f.stage.h, f.image.h * scale, v.y),
  };
}

export const fitView = (f: ZoomFrame): ZoomView => clamp(f, { scale: fitScale(f), x: 0, y: 0 });

export const pan = (f: ZoomFrame, v: ZoomView, dx: number, dy: number): ZoomView =>
  clamp(f, { ...v, x: v.x + dx, y: v.y + dy });

export function zoomAt(f: ZoomFrame, v: ZoomView, p: Point, factor: number): ZoomView {
  const scale = clampScale(f, v.scale * factor);
  const k = scale / v.scale;
  return clamp(f, { scale, x: p.x - (p.x - v.x) * k, y: p.y - (p.y - v.y) * k });
}

export function toggle(f: ZoomFrame, v: ZoomView, p: Point): ZoomView {
  const fit = fitScale(f);
  if (Math.abs(v.scale - fit) <= TOGGLE_TOLERANCE * fit) {
    return zoomAt(f, v, p, oneScale(f) / v.scale);
  }
  return fitView(f);
}

const distance = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y);
const midpoint = (a: Point, b: Point): Point => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

export function pinch(
  f: ZoomFrame,
  v: ZoomView,
  before: [Point, Point],
  after: [Point, Point],
): ZoomView {
  const from = midpoint(...before);
  const to = midpoint(...after);
  const d0 = distance(...before);
  const scale = clampScale(f, d0 === 0 ? v.scale : (v.scale * distance(...after)) / d0);
  const k = scale / v.scale;
  // The image pixel under the old midpoint ends under the new one.
  return clamp(f, { scale, x: to.x - (from.x - v.x) * k, y: to.y - (from.y - v.y) * k });
}

/** A new image of the same picture (thumbnail to original) keeps its size and place on screen. */
export function swapImage(prevImage: Extent, f: ZoomFrame, v: ZoomView): ZoomView {
  return clamp(f, { scale: (v.scale * prevImage.w) / f.image.w, x: v.x, y: v.y });
}

export const zoomPercent = (f: ZoomFrame, v: ZoomView): number =>
  Math.round((v.scale / oneScale(f)) * 100);
